import React, {useMemo} from 'react'
import {Text, useRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import {useCurrentBasket} from '../../../../../hooks/use-current-basket'
import {CART_HREF} from '../../../../../constants'
import {HeaderShoppingBagIcon} from '../../../icons'
import {cartCtaRecipe} from './cart-cta.recipe'
import HeaderIconButton from './header-icon-button'
import HeaderIconLink from './header-icon-link'

/**
 * Header cart CTA.
 *
 * Links to the cart page and surfaces the current item count — sourced from
 * `useCurrentBasket`'s `derivedData.totalItems` — as a badge. The badge is
 * omitted entirely once the basket has no items, so an empty cart renders a
 * plain icon link.
 *
 * Rendered through `HeaderIconButton` for the round icon-button chrome
 * (36×36, hover, focus-ring); this component only owns the badge/icon
 * layout and the accessible name.
 *
 * @component
 * @returns {JSX.Element}
 */
const CartCta = () => {
    const {formatMessage} = useIntl()
    const {
        derivedData: {totalItems}
    } = useCurrentBasket()

    const recipe = useRecipe({recipe: cartCtaRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    // The accessible name MUST carry the count: an `aria-label` on the link
    // overrides its subtree, so folding the count into the label (as legacy
    // does) is the only way screen readers announce it. Matches the legacy
    // header's single count-bearing label for all states, including 0.
    const label = formatMessage(
        {
            id: 'header.button.assistive_msg.my_cart_with_num_items',
            defaultMessage: 'My cart, number of items: {numItems}'
        },
        {numItems: totalItems}
    )

    return (
        <HeaderIconButton as={HeaderIconLink} to={CART_HREF} aria-label={label}>
            <HeaderShoppingBagIcon />
            {totalItems > 0 && (
                <Text as="span" css={styles.badge} aria-hidden="true">
                    {totalItems}
                </Text>
            )}
        </HeaderIconButton>
    )
}

if (process.env.NODE_ENV !== 'production') {
    CartCta.displayName = 'CartCta'
}

export default CartCta
